import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** Adds a teal unread dot in the top-right. */
  dot?: boolean;
  /** Flat on a dark surface vs. floating over artwork. */
  variant?: 'floating' | 'flat';
  size?: 36 | 44;
  /** Required for a11y since the child is usually an icon-only SVG. */
  label: string;
}

const SIZE_CLASS: Record<36 | 44, string> = {
  36: 'size-9 rounded-[10px]',
  44: 'size-11 rounded-xl',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      children,
      dot,
      variant = 'floating',
      size = 36,
      label,
      className = '',
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        className={[
          'relative inline-flex items-center justify-center text-on-surface transition',
          'hover:text-primary',
          variant === 'floating'
            ? 'bg-black/40 backdrop-blur'
            : 'bg-surface-2 border border-[color:var(--color-border)]',
          SIZE_CLASS[size],
          className,
        ].join(' ')}
        {...rest}
      >
        {children}
        {dot && (
          <span
            aria-hidden
            className="absolute right-[8px] top-[7px] size-2 rounded-full border-2 border-black/40 bg-primary"
          />
        )}
      </button>
    );
  },
);
